// The five classic empirical equations for the speed of sound in seawater, so the
// SVP panel can recompute its profile from the stored temperature/salinity/depth
// under whichever standard the surveyor works to. They agree to ~0.5 m/s across the
// oceanic range and diverge slightly at depth/extremes — which is exactly why a
// survey tool lets you pick. All take T (°C), S (PSU), D (depth, m) and return m/s.
//
// Depth→pressure uses the standard P(dbar) ≈ depth(m) approximation (good to ~1%);
// each pressure-based equation converts to its own unit below. Verified numerically
// against the 1506.7 m/s benchmark at T=15 °C, S=35, D=0 before shipping.

export type SoundSpeedEq =
  | "chen-millero"
  | "del-grosso"
  | "mackenzie"
  | "wilson"
  | "coppens";

export interface EqInfo {
  key: SoundSpeedEq;
  label: string;
  note: string;
}

export const SOUND_SPEED_EQS: EqInfo[] = [
  { key: "chen-millero", label: "Chen–Millero (UNESCO)", note: "International CTD standard (1977)." },
  { key: "del-grosso", label: "Del Grosso (1974)", note: "Favoured in precise hydrography; best at depth." },
  { key: "mackenzie", label: "Mackenzie (1981)", note: "Simple 9-term; upper-ocean workhorse." },
  { key: "wilson", label: "Wilson (1960)", note: "Older US Navy equation; runs ~0.5 m/s high." },
  { key: "coppens", label: "Coppens (1981)", note: "Simplified; shallow / coastal." },
];

/** The default equation — the international CTD standard. */
export const DEFAULT_EQ: SoundSpeedEq = "chen-millero";

export function isSoundSpeedEq(v: string): v is SoundSpeedEq {
  return SOUND_SPEED_EQS.some((e) => e.key === v);
}

const barOf = (d: number): number => d / 10; // dbar → bar
const kgcm2Of = (d: number): number => d / 10 / 0.980665; // dbar → kg/cm²

/** Mackenzie (1981) 9-term equation — D in metres. */
function mackenzie(T: number, S: number, D: number): number {
  return (
    1448.96 + 4.591 * T - 5.304e-2 * T * T + 2.374e-4 * T * T * T +
    1.34 * (S - 35) + 1.63e-2 * D + 1.675e-7 * D * D -
    1.025e-2 * T * (S - 35) - 7.139e-13 * T * D * D * D
  );
}

/** Coppens (1981) — D in km, t = T/10. */
function coppens(T: number, S: number, D: number): number {
  const d = D / 1000;
  const t = T / 10;
  const c0 =
    1449.05 + 45.7 * t - 5.21 * t * t + 0.23 * t * t * t +
    (1.333 - 0.126 * t + 0.009 * t * t) * (S - 35);
  return (
    c0 + (16.23 + 0.253 * t) * d + (0.213 - 0.1 * t) * d * d +
    (0.016 + 0.0002 * (S - 35)) * (S - 35) * t * d
  );
}

/** Chen & Millero (1977), the UNESCO algorithm — P in bar. */
function chenMillero(T: number, S: number, D: number): number {
  const P = barOf(D);
  const Cw =
    (1402.388 + 5.0383 * T - 5.8109e-2 * T ** 2 + 3.3432e-4 * T ** 3 - 1.47797e-6 * T ** 4 + 3.1419e-9 * T ** 5) +
    (0.153563 + 6.8999e-4 * T - 8.1829e-6 * T ** 2 + 1.3632e-7 * T ** 3 - 6.126e-10 * T ** 4) * P +
    (3.126e-5 - 1.7111e-6 * T + 2.5986e-8 * T ** 2 - 2.5353e-10 * T ** 3 + 1.0415e-12 * T ** 4) * P ** 2 +
    (-9.7729e-9 + 3.8513e-10 * T - 2.3654e-12 * T ** 2) * P ** 3;
  const A =
    (1.389 - 1.262e-2 * T + 7.166e-5 * T ** 2 + 2.008e-6 * T ** 3 - 3.21e-8 * T ** 4) +
    (9.4742e-5 - 1.2583e-5 * T - 6.4928e-8 * T ** 2 + 1.0515e-8 * T ** 3 - 2.0142e-10 * T ** 4) * P +
    (-3.9064e-7 + 9.1061e-9 * T - 1.6009e-10 * T ** 2 + 7.994e-12 * T ** 3) * P ** 2 +
    (1.1e-10 + 6.651e-12 * T - 3.391e-13 * T ** 2) * P ** 3;
  const B = -1.922e-2 - 4.42e-5 * T + (7.3637e-5 + 1.7945e-7 * T) * P;
  const Dd = 1.727e-3 - 7.9836e-6 * P;
  return Cw + A * S + B * S ** 1.5 + Dd * S ** 2;
}

/** Del Grosso (1974) — P in kg/cm². */
function delGrosso(T: number, S: number, D: number): number {
  const P = kgcm2Of(D);
  const dCT = 5.01109398873 * T - 5.50946843172e-2 * T ** 2 + 2.2153596924e-4 * T ** 3;
  const dCS = 1.32952290781 * S + 1.28955756844e-4 * S ** 2;
  const dCP = 0.156059257041 * P + 2.44998688441e-5 * P ** 2 - 8.83392332513e-9 * P ** 3;
  const dCSTP =
    -1.27562300034e-2 * T * S + 6.35191613389e-3 * T * P +
    2.65484716608e-8 * T ** 2 * P ** 2 - 1.59349479045e-6 * T * P ** 2 +
    5.22116437235e-10 * T * P ** 3 - 4.38031096213e-7 * T ** 3 * P -
    1.61674495909e-9 * S ** 2 * P ** 2 + 9.6840315641e-5 * T ** 2 * S +
    4.85639620015e-6 * T * S ** 2 * P - 3.40597039004e-4 * T * S * P;
  return 1402.392 + dCT + dCS + dCP + dCSTP;
}

/** Wilson (1960) — P in kg/cm². */
function wilson(T: number, S: number, D: number): number {
  const P = kgcm2Of(D);
  const dS = S - 35;
  const cT = 4.5721 * T - 4.4532e-2 * T ** 2 - 2.6045e-4 * T ** 3 + 7.9851e-6 * T ** 4;
  const cP = 1.60272e-1 * P + 1.0268e-5 * P ** 2 + 3.5216e-9 * P ** 3 - 3.3603e-12 * P ** 4;
  const cS = 1.39799 * dS + 1.69202e-3 * dS ** 2;
  const cSTP =
    dS * (-1.1244e-2 * T + 7.7711e-7 * T ** 2 + 7.7016e-5 * P - 1.2943e-7 * P ** 2 + 3.158e-8 * P * T + 1.579e-9 * P * T ** 2) +
    P * (-1.8607e-4 * T + 7.4812e-6 * T ** 2 + 4.5283e-8 * T ** 3) +
    P ** 2 * (-2.5294e-7 * T + 1.8563e-9 * T ** 2) +
    P ** 3 * (-1.9646e-10 * T);
  return 1449.14 + cT + cS + cP + cSTP;
}

/** Speed of sound (m/s) under the chosen equation, from T (°C), S (PSU), D (m). */
export function soundSpeed(eq: SoundSpeedEq, T: number, S: number, D: number): number {
  switch (eq) {
    case "chen-millero":
      return chenMillero(T, S, D);
    case "del-grosso":
      return delGrosso(T, S, D);
    case "mackenzie":
      return mackenzie(T, S, D);
    case "wilson":
      return wilson(T, S, D);
    case "coppens":
      return coppens(T, S, D);
  }
}
